import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Users, Briefcase, UserCheck, Building2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import LeadGenerator from '../components/LeadGenerator';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Home = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Referral Banner */}
      <div className="bg-[#d4a574] text-[#0a1628] py-2 text-center">
        <p className="text-sm font-medium">
          Know a company hiring? <Link to="/contact?type=referral" className="underline font-bold hover:text-[#1a2840]">Earn a referral fee when we place a candidate</Link>
        </p>
      </div>

      {/* Hero Section */}
      <section 
        className="relative min-h-[600px] flex items-center justify-center text-white pt-20"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1758518730162-09a142505bfd)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-[#0a1628]/95 to-[#0a1628]/80"></div>
        <div className="relative container mx-auto px-6 text-center z-10 py-16">
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-6 leading-tight break-words">
            Helping Veterans Land Careers<br />
            <span className="text-[#d4a574]">and Companies Hire Proven Talent</span>
          </h1>
          <p className="text-lg sm:text-xl md:text-2xl mb-4 text-gray-200 max-w-3xl mx-auto px-4">
            Resume support, LinkedIn optimization, and interview coaching for veterans.
          </p>
          <p className="text-lg sm:text-xl md:text-2xl mb-8 text-gray-200 max-w-3xl mx-auto px-4">
            Direct access to disciplined, high-performing talent for employers.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center px-4">
            <Link to="/contact?type=resume">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-[#0a1628] text-lg px-8 py-6 w-full sm:w-auto">
                I'm Looking for a Job
              </Button>
            </Link>
            <Link to="/contact?type=hire">
              <Button size="lg" className="bg-[#d4a574] text-[#0a1628] hover:bg-[#c49564] text-lg px-8 py-6 w-full sm:w-auto">
                I Want to Hire Talent <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
            <Link to="/contact?type=referral">
              <Button size="lg" variant="outline" className="border-2 border-[#d4a574] text-[#d4a574] hover:bg-[#d4a574] hover:text-[#0a1628] text-lg px-8 py-6 w-full sm:w-auto relative">
                <Users className="mr-2" size={20} />
                Submit a Referral
                <span className="absolute -top-2 -right-2 bg-[#d4a574] text-[#0a1628] text-xs px-2 py-1 rounded-full font-bold">EARN $</span>
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Section 2 - Who We Help */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12">
            {/* For Veterans */}
            <Card className="border-2 border-gray-200 hover:border-[#d4a574] transition-all duration-300 hover:shadow-xl">
              <CardContent className="p-8">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-[#0a1628] rounded-full mb-6">
                  <UserCheck className="text-[#d4a574]" size={32} />
                </div>
                <h3 className="text-2xl font-bold text-[#0a1628] mb-4">For Veterans & Transitioning Service Members</h3>
                <p className="text-lg text-gray-700 mb-6">
                  You've got the experience. We help you turn it into opportunity.
                </p>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-start space-x-3">
                    <CheckCircle className="text-[#d4a574] flex-shrink-0 mt-0.5" size={20} />
                    <span className="text-gray-700">Resume development and optimization</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <CheckCircle className="text-[#d4a574] flex-shrink-0 mt-0.5" size={20} />
                    <span className="text-gray-700">LinkedIn profiles that attract attention</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <CheckCircle className="text-[#d4a574] flex-shrink-0 mt-0.5" size={20} />
                    <span className="text-gray-700">Interview coaching that builds confidence</span>
                  </li>
                </ul>
                <p className="text-sm text-gray-500 italic mb-6">No resume? No problem. We'll help you start from scratch</p>
                <Link to="/contact?type=resume">
                  <Button className="w-full bg-[#0a1628] text-white hover:bg-[#1a2840]">
                    I'm Looking for a Job <ArrowRight className="ml-2" size={18} />
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* For Employers */}
            <Card className="border-2 border-[#d4a574] shadow-xl">
              <CardContent className="p-8">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-[#d4a574] rounded-full mb-6">
                  <Building2 className="text-[#0a1628]" size={32} />
                </div>
                <h3 className="text-2xl font-bold text-[#0a1628] mb-4">For Employers</h3>
                <p className="text-lg text-gray-700 mb-6">
                  You don't need more applicants. You need better ones.
                </p>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-start space-x-3">
                    <CheckCircle className="text-[#d4a574] flex-shrink-0 mt-0.5" size={20} />
                    <span className="text-gray-700">Access disciplined, mission-ready talent</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <CheckCircle className="text-[#d4a574] flex-shrink-0 mt-0.5" size={20} />
                    <span className="text-gray-700">Candidates with leadership, accountability, and work ethic</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <CheckCircle className="text-[#d4a574] flex-shrink-0 mt-0.5" size={20} />
                    <span className="text-gray-700">More confident hiring decisions</span>
                  </li>
                </ul>
                <Link to="/contact?type=hire">
                  <Button className="w-full bg-[#d4a574] text-[#0a1628] hover:bg-[#c49564]">
                    I Want to Hire Talent <ArrowRight className="ml-2" size={18} />
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Lead Generator Section - Resume Score */}
      <LeadGenerator />

      {/* Section 3 - The Problem */}
      <section className="py-20 bg-[#0a1628] text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="grid md:grid-cols-2 gap-8 mb-12">
              <div className="p-8 bg-[#1a2840] rounded-lg">
                <h3 className="text-xl font-bold text-[#d4a574] mb-4">For Veterans:</h3>
                <p className="text-lg text-gray-300">
                  Struggling to translate your military experience into civilian opportunities?
                </p>
              </div>
              <div className="p-8 bg-[#1a2840] rounded-lg">
                <h3 className="text-xl font-bold text-[#d4a574] mb-4">For Employers:</h3>
                <p className="text-lg text-gray-300">
                  Tired of sorting through candidates who don't meet expectations?
                </p>
              </div>
            </div>
            <p className="text-2xl font-bold text-[#d4a574]">
              That's where we can help
            </p>
          </div>
        </div>
      </section>

      {/* Section 4 - The Solution */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold text-[#0a1628] mb-6">
              Service2Success Talent Solutions connects the gap
            </h2>
            <p className="text-xl text-gray-700 mb-8">
              We help veterans clearly position their experience for today's job market, while helping employers identify and hire talent they can trust.
            </p>
            <p className="text-2xl font-bold text-[#d4a574]">
              No guesswork, no wasted time
            </p>
          </div>
        </div>
      </section>

      {/* Section 5 - How It Works */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#0a1628] mb-4">Simple, Clear, Effective</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-[#d4a574] text-[#0a1628] rounded-full mb-4 text-2xl font-bold">
                1
              </div>
              <h3 className="text-xl font-bold text-[#0a1628] mb-2">Submit your information</h3>
            </div>
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-[#d4a574] text-[#0a1628] rounded-full mb-4 text-2xl font-bold">
                2
              </div>
              <h3 className="text-xl font-bold text-[#0a1628] mb-2">We refine, prepare, or match</h3>
            </div>
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-[#d4a574] text-[#0a1628] rounded-full mb-4 text-2xl font-bold">
                3
              </div>
              <h3 className="text-xl font-bold text-[#0a1628] mb-2">You get results</h3>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#0a1628] text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold mb-4">How can we help you today?</h2>
            <p className="text-xl text-gray-300 mb-8">Choose the path that fits you best</p>
            
            {/* Primary CTA - Highlighted */}
            <div className="mb-6">
              <Link to="/contact?type=hire">
                <Button size="lg" className="w-full sm:w-auto bg-[#d4a574] text-[#0a1628] hover:bg-[#c49564] py-8 px-12 text-xl font-bold shadow-lg">
                  I Want to Hire Talent <ArrowRight className="ml-2" size={24} />
                </Button>
              </Link>
            </div>
            
            {/* Secondary CTAs */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Link to="/contact?type=resume">
                <Button size="lg" variant="outline" className="w-full border-2 border-white text-white hover:bg-white hover:text-[#0a1628] py-6">
                  I'm Looking for a Job
                </Button>
              </Link>
              <Link to="/contact?type=referral">
                <Button size="lg" variant="outline" className="w-full border-2 border-[#d4a574] text-[#d4a574] hover:bg-[#d4a574] hover:text-[#0a1628] py-6 relative">
                  <Users className="mr-2" size={18} />
                  Submit a Referral
                  <span className="absolute -top-2 -right-2 bg-[#d4a574] text-[#0a1628] text-xs px-2 py-1 rounded-full font-bold">EARN $</span>
                </Button>
              </Link>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="w-full border-2 border-gray-400 text-gray-300 hover:bg-gray-700 py-6">
                  General Inquiry
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Section 6 - Why S2S */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-[#0a1628] mb-6">We go beyond the resume</h2>
            </div>
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <p className="text-lg text-gray-700 mb-6">
                  We take the time to understand the person behind the experience, especially when it comes to veterans transitioning into the civilian workforce.
                </p>
                <p className="text-lg text-gray-700 mb-6">
                  We help translate military experience into language employers understand, identify strengths that are often overlooked, and prepare candidates so they show up confident and ready.
                </p>
                <p className="text-lg text-gray-700 mb-8">
                  For employers, that means better-prepared candidates, fewer surprises, and stronger hires.
                </p>
                <ul className="space-y-4">
                  <li className="flex items-start space-x-3">
                    <CheckCircle className="text-[#d4a574] flex-shrink-0 mt-0.5" size={24} />
                    <span className="text-gray-700 font-medium">Veteran-focused and mission-driven</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <CheckCircle className="text-[#d4a574] flex-shrink-0 mt-0.5" size={24} />
                    <span className="text-gray-700 font-medium">Experience in both staffing and corporate recruiting</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <CheckCircle className="text-[#d4a574] flex-shrink-0 mt-0.5" size={24} />
                    <span className="text-gray-700 font-medium">Real candidate preparation, not just introductions</span>
                  </li>
                </ul>
              </div>
              {/* Transition Image - From Service to Success */}
              <div className="relative">
                <img 
                  src="https://customer-assets.emergentagent.com/job_f3eb41f1-84b7-4819-9f0d-d426d75fa7e9/artifacts/kewsc3il_33193.png"
                  alt="From Service to Success - Military to Corporate Transition"
                  className="rounded-lg shadow-2xl w-full"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 bg-gray-50 border-t-4 border-[#d4a574]">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="grid md:grid-cols-2 gap-4 mb-6">
              <p className="text-2xl md:text-3xl font-bold text-[#0a1628]">Don't miss the right opportunity</p>
              <p className="text-2xl md:text-3xl font-bold text-[#d4a574]">Don't miss the right candidate</p>
            </div>
            <p className="text-xl text-gray-600 mb-8">Take the next step</p>
            
            {/* Primary CTA - Highlighted */}
            <div className="mb-6">
              <Link to="/contact?type=hire">
                <Button size="lg" className="w-full sm:w-auto bg-[#d4a574] text-[#0a1628] hover:bg-[#c49564] py-8 px-12 text-xl font-bold shadow-lg">
                  I Want to Hire Talent <ArrowRight className="ml-2" size={24} />
                </Button>
              </Link>
            </div>
            
            {/* Secondary CTAs */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Link to="/contact?type=resume">
                <Button size="lg" variant="outline" className="w-full border-2 border-[#0a1628] text-[#0a1628] hover:bg-[#0a1628] hover:text-white py-6">
                  I'm Looking for a Job
                </Button>
              </Link>
              <Link to="/contact?type=referral">
                <Button size="lg" variant="outline" className="w-full border-2 border-[#d4a574] text-[#0a1628] hover:bg-[#d4a574] hover:text-[#0a1628] py-6 relative">
                  <Users className="mr-2" size={18} />
                  Submit a Referral
                  <span className="absolute -top-2 -right-2 bg-[#d4a574] text-[#0a1628] text-xs px-2 py-1 rounded-full font-bold">EARN $</span>
                </Button>
              </Link>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="w-full border-2 border-gray-400 text-gray-600 hover:bg-gray-200 py-6">
                  General Inquiry
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
